// docs/116 CI5: what the room makes of what just happened at the dice table. Pure: the zoomed table,
// the room's guests and the sound director all read the same answer from the same two views, so every
// client shows the same words (picked by the throw) at the same moment.
import { handName, handValue, hashInts } from './dice';
import { throwEnd } from './diceMotion';
import type { DiceView } from './types';

export type ReactionKind = 'cheer' | 'gasp' | 'groan' | 'laugh' | 'phew' | 'win' | 'kitty';

export interface Reaction {
  kind: ReactionKind;
  /** The seat it is about (the thrower, or the first winner); −1 none. */
  seat: number;
  /** Words over that seat. */
  line: string;
  /** Words the others at the table and the watchers may shout (pick by index). */
  chorus: string[];
  /** "Ooh…" said as the throw leaves the hand (the last push). */
  ooh?: boolean;
  /** How long after the view arrived it lands (the throw's settle), ms. */
  delayMs: number;
  /** Seeds every choice about it (words, sound variants). */
  seed: number;
  /** A big one: Three Crowns, the Gallows, a bust on the last push, a win. */
  big: boolean;
}

/** The big lettering of a hand's end or a settle. */
export interface DiceResult {
  text: string;
  tone: 'gold' | 'red' | 'plain';
  seat: number;
  delayMs: number;
  /** Changes for each new result (a key for the animation). */
  key: string;
}

const pick = <T,>(xs: readonly T[], seed: number, k = 0): T => xs[hashInts(seed, k) % xs.length]!;

const CHEER = ['Crowns!', 'Huzzah!', 'Well thrown!', 'Ha! Lovely!', 'Look at that!', 'Drinks on you!'];
const GROAN = ['Ohhh…', 'Rotten luck.', 'Bones bit you!', 'Unlucky!', 'Oof.'];
const LAUGH = ['Ha ha ha!', 'Greedy!', 'Should\'ve stood!', 'The bones giveth…', 'Hah!'];
const GASP = ['The Gallows!', 'Gods!', 'Three skulls!', 'Hang him!', 'Ooh, cursed!'];
const WIN = ['Pay up!', 'Well played!', 'Huzzah!', 'Next round on you!'];

const eventKey = (v: DiceView) => `${v.gameN}|${v.throwN}|${v.last}|${v.turn}|${v.state}|${v.turn >= 0 ? v.seats[v.turn]?.state : ''}`;

/** Something new happened at the table between these views (not a mere tick). */
export function changed(prev: DiceView | undefined, next: DiceView): boolean {
  return !!prev && eventKey(prev) !== eventKey(next);
}

/**
 * docs/121 VW8: only the turn moved on (the pause after a hand ran out and the next seat is up): `last`
 * still names how the last hand ended, so it is not a new result or a new reaction.
 */
export function turnOnly(prev: DiceView, next: DiceView): boolean {
  return prev.gameN === next.gameN && prev.throwN === next.throwN && prev.last === next.last && prev.state === next.state && prev.turn !== next.turn;
}

function landing(prev: DiceView, next: DiceView): number {
  return next.throwN !== prev.throwN ? throwEnd(next.throwN, next.thrown) : 0;
}

/** The room's reaction to what happened between `prev` and `next`, if it is worth one. */
export function reactionOf(prev: DiceView | undefined, next: DiceView): Reaction | undefined {
  if (!prev || !changed(prev, next)) return undefined;
  if (turnOnly(prev, next)) return undefined;
  const seed = hashInts(next.throwN, next.gameN, next.turn + 1, next.last.length);
  const delayMs = landing(prev, next);
  const seat = next.turn;
  const name = seat >= 0 ? next.seats[seat]?.name ?? '' : '';
  const lastPush = next.throwN !== prev.throwN && prev.pushes === 1 && prev.turn === next.turn && next.thrown !== 0b111;
  const r = (kind: ReactionKind, line: string, chorus: readonly string[], big: boolean): Reaction => ({ kind, seat, line, chorus: [pick(chorus, seed, 1), pick(chorus, seed, 2), pick(chorus, seed, 3)], ...(lastPush ? { ooh: true } : {}), delayMs, seed, big });
  switch (next.last) {
    case 'crowns': return r('cheer', 'THREE CROWNS!', CHEER, true);
    case 'hearth': return r('cheer', pick(['The Hearth!', 'Warm hands!', 'Four, five, six!'], seed), ['The Hearth!', ...CHEER.slice(1)], false);
    case 'triple': {
      const face = next.dice[0] || next.seats[seat]?.hand[0] || 0;
      return r('cheer', `${handName([face, face, face])}!`, CHEER.slice(1), false);
    }
    case 'gallows': return r('gasp', pick(['No…', 'Not the Gallows!', 'Curse these bones!'], seed), GASP, true);
    case 'bust': return lastPush
      ? r('laugh', pick(['NO!', 'Oh, come ON!', 'I had it!'], seed), LAUGH, true)
      : r(hashInts(seed, 9) % 2 ? 'groan' : 'laugh', pick(['Blast!', 'Bah!', 'Skull!'], seed), hashInts(seed, 9) % 2 ? GROAN : LAUGH, false);
    case 'stood': case 'timeout': {
      if (!lastPush) return undefined;
      const score = next.seats[seat]?.score ?? 0;
      return r('phew', score >= 15 ? 'Ha! Safe!' : 'Phew…', ['Ooh…', 'Brave!', 'Lucky!'], false);
    }
    case 'won': {
      if (prev.state === 'settling') return undefined;
      const w = next.winners;
      const first = w[0] ?? -1;
      const who = next.seats[first]?.name ?? name;
      const won = next.seats[first]?.won ?? 0;
      return { kind: 'win', seat: first, line: won >= 10 ? 'Ha HA!' : pick(['Mine!', 'Come to me!', 'Lovely!'], seed), chorus: [pick(WIN, seed, 1), `Well played, ${who.split(' ')[0]}!`, pick(WIN, seed, 3)], delayMs: 0, seed, big: won >= 10 || w.length > 1 };
    }
    case 'kitty':
      if (prev.state === 'settling') return undefined;
      return { kind: 'kitty', seat: -1, line: '', chorus: ['All bust!', 'The kitty grows…', 'Nobody?', 'Ha! The house laughs.'], delayMs: 0, seed, big: false };
    default: return undefined;
  }
}

/** The big lettering for what happened between `prev` and `next` (every hand's end, the settle). */
export function resultOf(prev: DiceView | undefined, next: DiceView): DiceResult | undefined {
  if (!prev || !changed(prev, next) || turnOnly(prev, next)) return undefined;
  const key = `${next.gameN}:${next.throwN}:${next.last}:${next.turn}`;
  const delayMs = landing(prev, next);
  const seat = next.turn;
  const s = seat >= 0 ? next.seats[seat] : undefined;
  const hand = s?.hand.some((d) => d) ? s.hand : next.dice;
  switch (next.last) {
    case 'crowns': return { text: 'Three Crowns!', tone: 'gold', seat, delayMs, key };
    case 'triple': return { text: `${handName(hand)}!`, tone: 'gold', seat, delayMs, key };
    case 'hearth': return { text: 'The Hearth!', tone: 'gold', seat, delayMs, key };
    case 'gallows': return { text: 'The Gallows!', tone: 'red', seat, delayMs, key };
    case 'bust': return { text: 'Bust!', tone: 'red', seat, delayMs, key };
    case 'stood': return { text: `Stands on ${handValue(hand)}`, tone: 'plain', seat, delayMs, key };
    case 'timeout': return { text: `Time! ${handValue(hand)}`, tone: 'plain', seat, delayMs, key };
    case 'started': return { text: `Stakes in — ${next.stake} a head`, tone: 'plain', seat: -1, delayMs: 0, key };
    case 'won': {
      if (prev.state === 'settling') return undefined;
      const ws = next.winners.map((i) => next.seats[i]!);
      const text = ws.length === 1 ? `${ws[0]!.name} takes ${ws[0]!.won ?? 0} gold!` : `Split pot! ${ws.map((w) => w.name.split(' ')[0]).join(' & ')}`;
      return { text, tone: 'gold', seat: next.winners[0] ?? -1, delayMs: 0, key };
    }
    case 'kitty':
      if (prev.state === 'settling') return undefined;
      return { text: `All bust! ${next.kitty} to the kitty`, tone: 'red', seat: -1, delayMs: 0, key };
    default: return undefined;
  }
}
